// File: src/pages/Salesreport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Alert,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import {
  FaFilter,
  FaChevronRight,
  FaChevronDown,
  FaSyncAlt,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import {
  _GetById,
  createOrderhistoryview,
} from "../import/ApiEndPoint/ApiCalling";
import { View_Invoice_Data_by_id } from "../import/ApiEndPoint/Api";

/* ---------------- page-scoped CSS ---------------- */
const injectOnce = () => {
  if (document.getElementById("salesreport-inline-css-v2")) return;
  const style = document.createElement("style");
  style.id = "salesreport-inline-css-v2";
  style.innerHTML = `
    .sr-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .sr-card{
      border-radius:14px;
      border:1px solid #e5e7eb;
      background:#fff;
    }
    .sr-toolbar-card{
      padding-top:4px;
      padding-bottom:4px;
      border-radius:14px;
      border:1px solid #e5e7eb;
      background:#f4f7f6;
    }
    .sr-table-wrap{
      width:100%;
      overflow:auto;
      border:1px solid #e5e7eb;
      border-radius:14px;
      background:#fff;
    }
    .sr-table{
      width:100%;
      border-collapse:collapse;
      table-layout:auto;
      min-width:1180px;
    }
    .sr-table thead th{
      background:#f4f7f6;
      color:#0f172a;
      font-weight:700;
      text-transform:uppercase;
      font-size:12px;
      letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb;
      padding:8px 10px;
      position:sticky;
      top:0;
      z-index:1;
      text-align:center;
      white-space:nowrap;
    }
    .sr-table tbody td{
      padding:8px 10px;
      border-bottom:1px solid #f1f5f9;
      vertical-align:middle;
      color:#0f172a;
      font-size:14px;
      text-align:center;
    }
    .sr-table tfoot td{
      padding:8px 10px;
      font-weight:800;
      background:#fafafa;
      border-top:1px solid #e5e7eb;
      text-align:center;
    }
    .sr-table tbody tr:hover{
      background:#f9fbff;
    }
    .sr-month-row{
      background:#eef6ff !important;
      font-weight:800;
      cursor:pointer;
    }
    .sr-month-row:hover{
      background:#e4efff !important;
    }
    .sr-invoice-row{
      background:#fff;
      cursor:pointer;
    }
    .sr-product-row{
      background:#fcfcfd;
    }
    .sr-month-name,
    .sr-invoice-name{
      display:flex;
      align-items:center;
      gap:8px;
      justify-content:flex-start;
    }
    .sr-link{
      color:#1d4ed8;
      text-decoration:underline;
      cursor:pointer;
      font-weight:700;
    }
    .sr-muted{
      color:#64748b;
      font-size:12px;
      font-weight:600;
    }
    .sr-loading-inline{
      display:inline-flex;
      align-items:center;
      gap:8px;
      color:#2563eb;
      font-weight:700;
    }
    .picklist{
      border:1px solid #e5e7eb;
      border-radius:10px;
      background:#fff;
      padding:8px;
      max-height:300px;
      overflow:auto;
    }
    .pickitem{
      padding:8px 10px;
      border:1px solid #e5e7eb;
      border-radius:8px;
      margin-bottom:6px;
      background:#fff;
      cursor:pointer;
    }
  `;
  document.head.appendChild(style);
};

/* ---------------- helpers ---------------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const N = (v) => (isNaN(Number(v)) ? 0 : Number(v));
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (v) => round2(v).toFixed(2);
const dateOnly = (d) => (d ? String(d).split("T")[0] : "");
const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const firstNum = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
};

function getFinancialYearString(dateObj) {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function getFinancialYearStartYear(fy) {
  const n = Number(String(fy).split("-")[0]);
  return Number.isFinite(n) ? n : new Date().getFullYear();
}

function getFYMonthMeta(dateObj) {
  const d = new Date(dateObj);
  const month = d.getMonth() + 1;
  const fy = getFinancialYearString(d);

  const labelMap = {
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
    1: "January",
    2: "February",
    3: "March",
  };

  const sortIndexMap = {
    4: 1,
    5: 2,
    6: 3,
    7: 4,
    8: 5,
    9: 6,
    10: 7,
    11: 8,
    12: 9,
    1: 10,
    2: 11,
    3: 12,
  };

  const fyStart = getFinancialYearStartYear(fy);
  const displayYear = month >= 4 ? fyStart : fyStart + 1;

  return {
    fy,
    month,
    label: `${labelMap[month]} ${displayYear}`,
    sortIndex: sortIndexMap[month],
  };
}

const MONTHS_FY = [
  { key: 4, label: "April" },
  { key: 5, label: "May" },
  { key: 6, label: "June" },
  { key: 7, label: "July" },
  { key: 8, label: "August" },
  { key: 9, label: "September" },
  { key: 10, label: "October" },
  { key: 11, label: "November" },
  { key: 12, label: "December" },
  { key: 1, label: "January" },
  { key: 2, label: "February" },
  { key: 3, label: "March" },
];

const QUARTERS = [
  { value: "Q1", label: "Q1 (Apr - Jun)", months: [4, 5, 6] },
  { value: "Q2", label: "Q2 (Jul - Sep)", months: [7, 8, 9] },
  { value: "Q3", label: "Q3 (Oct - Dec)", months: [10, 11, 12] },
  { value: "Q4", label: "Q4 (Jan - Mar)", months: [1, 2, 3] },
];

const HALVES = [
  { value: "H1", label: "H1 (Apr - Sep)", months: [4, 5, 6, 7, 8, 9] },
  { value: "H2", label: "H2 (Oct - Mar)", months: [10, 11, 12, 1, 2, 3] },
];

/* ---------------- invoice summary normalization ---------------- */
function getInvoiceNo(order, prefix = "", suffix = "") {
  const inv =
    order?.invoiceId ??
    order?.InvoiceId ??
    order?.invoiceNo ??
    order?.Invoice_No ??
    order?.SalesInvoiceNo ??
    "";
  return inv ? `${prefix}${inv}${suffix}` : "-";
}

function getOrderTotals(order) {
  const basic = firstNum(
    order?.amount,
    order?.Amount,
    order?.taxableAmount,
    order?.basicTotal,
    order?.BasicTotal,
  );
  const cgst = firstNum(
    order?.cgstTotal,
    order?.CGSTTotal,
    order?.cgst,
    order?.CGST,
  );
  const sgst = firstNum(
    order?.sgstTotal,
    order?.SGSTTotal,
    order?.sgst,
    order?.SGST,
  );
  const igst = firstNum(
    order?.igstTotal,
    order?.IGSTTotal,
    order?.igst,
    order?.IGST,
  );
  const roundOff = firstNum(
    order?.roundOff,
    order?.roundoff,
    order?.RoundOff,
    order?.round_off,
    order?.Round_Off,
    0,
  );
  const grand = firstNum(
    order?.grandTotal,
    order?.GrandTotal,
    order?.grand_total,
    order?.totalAmount,
    order?.TotalAmount,
  );

  return {
    basic: round2(N(basic)),
    cgst: round2(N(cgst)),
    sgst: round2(N(sgst)),
    igst: round2(N(igst)),
    roundOff: round2(N(roundOff)),
    grand: round2(N(grand)),
  };
}

function normalizeInvoices(orders, prefix = "", suffix = "") {
  return (orders || []).map((ord) => {
    const totals = getOrderTotals(ord);
    const rowDate = ord?.date || ord?.updatedAt || ord?.createdAt || "";
    return {
      _id: ord?._id,
      invoiceId: ord?.invoiceId,
      _fullInvoice: getInvoiceNo(ord, prefix, suffix),
      _rowDate: rowDate,
      _dateOnly: dateOnly(rowDate),
      _partyName: ord?.partyId?.CompanyName || ord?.fullName || "-",
      _status: ord?.status || "",
      _totals: totals,
      _rawOrder: ord,
    };
  });
}

function groupInvoicesByMonth(rows = []) {
  const bucket = {};
  rows.forEach((r) => {
    const meta = getFYMonthMeta(r?._rowDate);
    const key = `${meta.fy}-${meta.month}`;
    if (!bucket[key]) {
      bucket[key] = {
        key,
        label: meta.label,
        month: meta.month,
        fy: meta.fy,
        sortIndex: meta.sortIndex,
        rows: [],
      };
    }
    bucket[key].rows.push(r);
  });

  return Object.values(bucket)
    .map((g) => {
      const totals = {
        basic: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        roundOff: 0,
        grand: 0,
      };
      g.rows.forEach((r) => {
        totals.basic += N(r?._totals?.basic);
        totals.cgst += N(r?._totals?.cgst);
        totals.sgst += N(r?._totals?.sgst);
        totals.igst += N(r?._totals?.igst);
        totals.roundOff += N(r?._totals?.roundOff);
        totals.grand += N(r?._totals?.grand);
      });

      return {
        ...g,
        rows: g.rows.sort(
          (a, b) => new Date(b?._rowDate || 0) - new Date(a?._rowDate || 0),
        ),
        totals,
      };
    })
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

/* ---------------- invoice details product wise ---------------- */
function getProductBasic(item) {
  return round2(
    firstNum(
      item?.taxableAmount,
      item?.BasicTotal,
      item?.basicTotal,
      item?.totalPrice,
      N(item?.qty) * N(item?.price),
      0,
    ) || 0,
  );
}

function normalizeInvoiceDetailProducts(printData) {
  const items = Array.isArray(printData?.orderItems)
    ? printData.orderItems
    : [];
  return items.map((it, idx) => ({
    _id: it?._id || `${idx}`,
    productName: it?.productId?.Product_Title || it?.Product_Title || "-",
    hsn: it?.productId?.HSN_Code || it?.HSN_Code || "",
    qty: N(it?.qty),
    unit: it?.primaryUnit || it?.unit || "",
    price: it?.price || "",
    basic: getProductBasic(it),
  }));
}

/* ---------------- columns ---------------- */
const BASE_COLS = [
  { key: "invoice", header: "Invoice No." },
  { key: "date", header: "Invoice Date" },
  { key: "party", header: "Party Name" },
  { key: "basic", header: "Basic Total" },
  { key: "cgst", header: "CGST" },
  { key: "sgst", header: "SGST" },
  { key: "igst", header: "IGST" },
  { key: "roundOff", header: "Round Off" },
  { key: "grand", header: "Grand Total" },
  { key: "status", header: "Status" },
];

export default function Salesreport() {
  const addBufferRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [fy, setFy] = useState("");
  const [periodType, setPeriodType] = useState("YEARLY");
  const [selectedMonth, setSelectedMonth] = useState(4);
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [selectedHalf, setSelectedHalf] = useState("H1");

  const [masterShow, setMasterShow] = useState(false);
  const [monthExpanded, setMonthExpanded] = useState({});
  const [invoiceExpanded, setInvoiceExpanded] = useState({});
  const [invoiceLoading, setInvoiceLoading] = useState({});
  const [invoiceDetailsMap, setInvoiceDetailsMap] = useState({});

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        if (user?.rolename?.roleName === "MASTER") setMasterShow(true);

        const inside = CheckPermission(
          "PARTY WISE AND PRODUCT WISE SALE REPORT",
        );
        if (inside && typeof inside === "object") {
          setPermissions((p) => ({ ...p, ...inside }));
        }

        const res = await createOrderhistoryview(user?._id);
        const completed = Array.isArray(res?.orderHistory)
          ? res.orderHistory.filter((o) =>
              String(o?.status || "")
                .toLowerCase()
                .includes("completed"),
            )
          : [];

        const company = JSON.parse(
          localStorage.getItem("Companydetail") || "{}",
        );
        const prefix = company?.Prefix || "";
        const suffix = company?.Suffix || "";

        const normalized = normalizeInvoices(completed, prefix, suffix);
        normalized.sort(
          (a, b) => new Date(b?._rowDate || 0) - new Date(a?._rowDate || 0),
        );

        setAllRows(normalized);

        const fySet = [
          ...new Set(
            normalized.map((r) => getFinancialYearString(r?._rowDate)),
          ),
        ].sort((a, b) => Number(b.split("-")[0]) - Number(a.split("-")[0]));
        setFy(fySet[0] || getFinancialYearString(new Date()));

        const saved = localStorage.getItem("PartywiseProductWiseSaleReport");
        const parsed = saved ? JSON.parse(saved) : null;
        if (Array.isArray(parsed) && parsed.length) {
          const keys = parsed
            .map((c) => c?.key)
            .filter((k) => BASE_COLS.some((b) => b.key === k));
          setSelectedKeys(keys.length ? keys : BASE_COLS.map((c) => c.key));
        } else {
          setSelectedKeys(BASE_COLS.map((c) => c.key));
        }
      } catch (e) {
        console.error("[Salesreport] load error:", e);
        setErrorMsg(
          e?.response?.data?.message || e?.message || "Failed to load data.",
        );
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fyOptions = useMemo(() => {
    const fySet = [
      ...new Set(allRows.map((r) => getFinancialYearString(r?._rowDate))),
    ].sort((a, b) => Number(b.split("-")[0]) - Number(a.split("-")[0]));
    return fySet;
  }, [allRows]);

  const visibleCols = useMemo(
    () => BASE_COLS.filter((c) => selectedKeys.includes(c.key)),
    [selectedKeys],
  );

  // product-aware searchable list
  const searchedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allRows;

    return allRows.filter((r) => {
      const invoiceWords = [r?._fullInvoice, r?._partyName, r?._status]
        .map(toStr)
        .join(" ")
        .toLowerCase();

      if (invoiceWords.includes(term)) return true;

      const detail = invoiceDetailsMap[r._id];
      if (detail?.products?.length) {
        const productWords = detail.products
          .map((p) =>
            [p?.productName, p?.hsn, p?.unit, p?.price].map(toStr).join(" "),
          )
          .join(" ")
          .toLowerCase();
        if (productWords.includes(term)) return true;
      }

      return false;
    });
  }, [allRows, search, invoiceDetailsMap]);

  const filteredRows = useMemo(() => {
    let data = searchedRows.slice();

    if (startDate || endDate) {
      const min = parseYMD(startDate) || new Date(-8640000000000000);
      const max = parseYMD(endDate);
      const maxIncl = max
        ? new Date(max.getFullYear(), max.getMonth(), max.getDate() + 1)
        : new Date(8640000000000000);

      data = data.filter((r) => {
        const d0 = r?._rowDate;
        const d = d0 ? new Date(d0) : null;
        return d && d >= min && d < maxIncl;
      });
    } else {
      if (fy) {
        data = data.filter((r) => getFinancialYearString(r?._rowDate) === fy);
      }

      if (periodType === "MONTHLY") {
        data = data.filter(
          (r) => getFYMonthMeta(r?._rowDate).month === Number(selectedMonth),
        );
      } else if (periodType === "QUARTERLY") {
        const q = QUARTERS.find((x) => x.value === selectedQuarter);
        const months = q?.months || [];
        data = data.filter((r) =>
          months.includes(getFYMonthMeta(r?._rowDate).month),
        );
      } else if (periodType === "HALF_YEARLY") {
        const h = HALVES.find((x) => x.value === selectedHalf);
        const months = h?.months || [];
        data = data.filter((r) =>
          months.includes(getFYMonthMeta(r?._rowDate).month),
        );
      }
    }

    return data;
  }, [
    searchedRows,
    startDate,
    endDate,
    fy,
    periodType,
    selectedMonth,
    selectedQuarter,
    selectedHalf,
  ]);

  const groupedRows = useMemo(
    () => groupInvoicesByMonth(filteredRows),
    [filteredRows],
  );

  useEffect(() => {
    const initial = {};
    groupedRows.forEach((g) => {
      initial[g.key] = false;
    });
    setMonthExpanded(initial);
  }, [groupedRows]);

  const grandTotals = useMemo(() => {
    const acc = {
      basic: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      roundOff: 0,
      grand: 0,
    };
    filteredRows.forEach((r) => {
      acc.basic += N(r?._totals?.basic);
      acc.cgst += N(r?._totals?.cgst);
      acc.sgst += N(r?._totals?.sgst);
      acc.igst += N(r?._totals?.igst);
      acc.roundOff += N(r?._totals?.roundOff);
      acc.grand += N(r?._totals?.grand);
    });
    return acc;
  }, [filteredRows]);

  const toggleMonth = (key) => {
    setMonthExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleInvoice = async (row) => {
    const id = row?._id;
    if (!id) return;

    const nextOpen = !invoiceExpanded[id];
    setInvoiceExpanded((prev) => ({ ...prev, [id]: nextOpen }));

    if (!nextOpen) return;
    if (invoiceDetailsMap[id]) return;

    try {
      setInvoiceLoading((prev) => ({ ...prev, [id]: true }));
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const database = user?.database;
      const invoiceId = row?.invoiceId || row?._rawOrder?.invoiceId;

      if (!invoiceId) {
        setInvoiceDetailsMap((prev) => ({
          ...prev,
          [id]: { error: "Invoice ID not found", products: [] },
        }));
        return;
      }

      const resp = await _GetById(View_Invoice_Data_by_id, database, invoiceId);

      const printData = resp?.printData;
      if (!printData) {
        setInvoiceDetailsMap((prev) => ({
          ...prev,
          [id]: { error: "Invoice data not found", products: [] },
        }));
        return;
      }

      const products = normalizeInvoiceDetailProducts(printData);
      setInvoiceDetailsMap((prev) => ({
        ...prev,
        [id]: { error: "", products },
      }));
    } catch (e) {
      console.error("Invoice detail fetch error:", e);
      setInvoiceDetailsMap((prev) => ({
        ...prev,
        [id]: { error: "Failed to load invoice details", products: [] },
      }));
    } finally {
      setInvoiceLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  /* ----- column chooser ----- */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);
  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    const toPersist = selectedKeys.map((k) => ({ key: k }));
    localStorage.setItem(
      "PartywiseProductWiseSaleReport",
      JSON.stringify(toPersist),
    );
    closeModal();
  };

  /* ----- exports now include invoice + products ----- */
  const buildExportRows = () => {
    const rows = [];

    groupedRows.forEach((group) => {
      rows.push({
        Type: "MONTH TOTAL",
        Month: group.label,
        "Invoice No.": "",
        "Invoice Date": "",
        "Party Name": "",
        Product: "",
        HSN: "",
        Qty: "",
        Unit: "",
        Price: "",
        "Basic Total": money(group.totals.basic),
        CGST: money(group.totals.cgst),
        SGST: money(group.totals.sgst),
        IGST: money(group.totals.igst),
        "Round Off": money(group.totals.roundOff),
        "Grand Total": money(group.totals.grand),
        Status: "",
      });

      group.rows.forEach((r) => {
        rows.push({
          Type: "INVOICE",
          Month: group.label,
          "Invoice No.": r?._fullInvoice,
          "Invoice Date": r?._dateOnly,
          "Party Name": r?._partyName,
          Product: "",
          HSN: "",
          Qty: "",
          Unit: "",
          Price: "",
          "Basic Total": money(r?._totals?.basic),
          CGST: money(r?._totals?.cgst),
          SGST: money(r?._totals?.sgst),
          IGST: money(r?._totals?.igst),
          "Round Off": money(r?._totals?.roundOff),
          "Grand Total": money(r?._totals?.grand),
          Status: r?._status,
        });

        const detail = invoiceDetailsMap[r._id];
        if (detail?.products?.length) {
          detail.products.forEach((p) => {
            rows.push({
              Type: "PRODUCT",
              Month: group.label,
              "Invoice No.": r?._fullInvoice,
              "Invoice Date": r?._dateOnly,
              "Party Name": r?._partyName,
              Product: p?.productName || "",
              HSN: p?.hsn || "",
              Qty: p?.qty ?? "",
              Unit: p?.unit || "",
              Price: p?.price || "",
              "Basic Total": money(p?.basic),
              CGST: "",
              SGST: "",
              IGST: "",
              "Round Off": "",
              "Grand Total": "",
              Status: r?._status,
            });
          });
        }
      });
    });

    rows.push({
      Type: "FINAL TOTAL",
      Month: "",
      "Invoice No.": "",
      "Invoice Date": "",
      "Party Name": "",
      Product: "",
      HSN: "",
      Qty: "",
      Unit: "",
      Price: "",
      "Basic Total": money(grandTotals.basic),
      CGST: money(grandTotals.cgst),
      SGST: money(grandTotals.sgst),
      IGST: money(grandTotals.igst),
      "Round Off": money(grandTotals.roundOff),
      "Grand Total": money(grandTotals.grand),
      Status: "",
    });

    return rows;
  };

  const exportXLSX = (fileName = "SalesReportPartyWise") => {
    const data = buildExportRows();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  const exportPDF = async () => {
    const data = buildExportRows();
    const headers = Object.keys(data[0] || {});
    const headerLine = headers.join(",");
    const lines = data.map((row) =>
      headers.map((h) => `"${toStr(row[h]).replace(/"/g, '""')}"`).join(","),
    );
    const csv = [headerLine, ...lines].join("\r\n");
    await exportDataToPDF(csv, "SalesReportPartyWise");
  };

  const noData = !groupedRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="sr-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="sr-toolbar-card">
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Party Wise Sale Report{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              {masterShow ? (
                <div style={{ flex: "1 1 240px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={(v) =>
                      localStorage.setItem(
                        "SuperadminIdByMaster",
                        JSON.stringify(v),
                      )
                    }
                    onSubmit={(e) => e.preventDefault()}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 120px" }} />
              )}

              <div style={{ minWidth: 260 }}>
                <Input
                  placeholder="Search invoice / party / product..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <div>
                <Label className="mb-0">Financial Year</Label>
                <Input
                  type="select"
                  value={fy}
                  onChange={(e) => setFy(e.target.value)}
                  style={{ minWidth: 160, background: "#fff", height: 32 }}
                >
                  {fyOptions.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </Input>
              </div>

              <div>
                <Label className="mb-0">Filter Type</Label>
                <Input
                  type="select"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  style={{ minWidth: 150, background: "#fff", height: 32 }}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="HALF_YEARLY">Half Yearly</option>
                  <option value="YEARLY">Yearly</option>
                </Input>
              </div>

              {periodType === "MONTHLY" && (
                <div>
                  <Label className="mb-0">Month</Label>
                  <Input
                    type="select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    style={{ minWidth: 150, background: "#fff", height: 32 }}
                  >
                    {MONTHS_FY.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {periodType === "QUARTERLY" && (
                <div>
                  <Label className="mb-0">Quarter</Label>
                  <Input
                    type="select"
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                    style={{ minWidth: 170, background: "#fff", height: 32 }}
                  >
                    {QUARTERS.map((q) => (
                      <option key={q.value} value={q.value}>
                        {q.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {periodType === "HALF_YEARLY" && (
                <div>
                  <Label className="mb-0">Half Year</Label>
                  <Input
                    type="select"
                    value={selectedHalf}
                    onChange={(e) => setSelectedHalf(e.target.value)}
                    style={{ minWidth: 170, background: "#fff", height: 32 }}
                  >
                    {HALVES.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              <span style={{ fontWeight: 700, opacity: 0.7 }}>OR</span>

              <div>
                <Label className="mb-0" style={{ fontSize: 12 }}>
                  Start Date
                </Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ height: 32 }}
                />
              </div>
              <div>
                <Label className="mb-0" style={{ fontSize: 12 }}>
                  End Date
                </Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ height: 32 }}
                />
              </div>

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={exportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => exportXLSX("SalesReportPartyWise")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card className="sr-card" style={{ padding: 8 }}>
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="sr-table-wrap">
                <table className="sr-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRows.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr
                          className="sr-month-row"
                          onClick={() => toggleMonth(group.key)}
                        >
                          {visibleCols.map((c, idx) => {
                            if (idx === 0) {
                              return (
                                <td key={c.key}>
                                  <div className="sr-month-name">
                                    {monthExpanded[group.key] ? (
                                      <FaChevronDown />
                                    ) : (
                                      <FaChevronRight />
                                    )}
                                    <span>{group.label}</span>
                                  </div>
                                </td>
                              );
                            }
                            if (c.key === "party")
                              return <td key={c.key}>Month Total</td>;
                            if (c.key === "basic")
                              return (
                                <td key={c.key}>{money(group.totals.basic)}</td>
                              );
                            if (c.key === "cgst")
                              return (
                                <td key={c.key}>{money(group.totals.cgst)}</td>
                              );
                            if (c.key === "sgst")
                              return (
                                <td key={c.key}>{money(group.totals.sgst)}</td>
                              );
                            if (c.key === "igst")
                              return (
                                <td key={c.key}>{money(group.totals.igst)}</td>
                              );
                            if (c.key === "roundOff")
                              return (
                                <td key={c.key}>
                                  {money(group.totals.roundOff)}
                                </td>
                              );
                            if (c.key === "grand")
                              return (
                                <td key={c.key}>{money(group.totals.grand)}</td>
                              );
                            return <td key={c.key}></td>;
                          })}
                        </tr>

                        {monthExpanded[group.key] &&
                          group.rows.map((row) => {
                            const isOpen = !!invoiceExpanded[row._id];
                            const isLoad = !!invoiceLoading[row._id];
                            const detail = invoiceDetailsMap[row._id];

                            return (
                              <React.Fragment key={row._id}>
                                <tr
                                  className="sr-invoice-row"
                                  onClick={() => toggleInvoice(row)}
                                >
                                  {visibleCols.map((c, idx) => {
                                    if (c.key === "invoice") {
                                      return (
                                        <td key={c.key}>
                                          <div className="sr-invoice-name">
                                            {isOpen ? (
                                              <FaChevronDown />
                                            ) : (
                                              <FaChevronRight />
                                            )}
                                            <span className="sr-link">
                                              {row._fullInvoice}
                                            </span>
                                          </div>
                                        </td>
                                      );
                                    }
                                    if (c.key === "date")
                                      return (
                                        <td key={c.key}>{row._dateOnly}</td>
                                      );
                                    if (c.key === "party")
                                      return (
                                        <td key={c.key}>{row._partyName}</td>
                                      );
                                    if (c.key === "basic")
                                      return (
                                        <td key={c.key}>
                                          {money(row?._totals?.basic)}
                                        </td>
                                      );
                                    if (c.key === "cgst")
                                      return (
                                        <td key={c.key}>
                                          {money(row?._totals?.cgst)}
                                        </td>
                                      );
                                    if (c.key === "sgst")
                                      return (
                                        <td key={c.key}>
                                          {money(row?._totals?.sgst)}
                                        </td>
                                      );
                                    if (c.key === "igst")
                                      return (
                                        <td key={c.key}>
                                          {money(row?._totals?.igst)}
                                        </td>
                                      );
                                    if (c.key === "roundOff")
                                      return (
                                        <td key={c.key}>
                                          {money(row?._totals?.roundOff)}
                                        </td>
                                      );
                                    if (c.key === "grand")
                                      return (
                                        <td key={c.key}>
                                          {money(row?._totals?.grand)}
                                        </td>
                                      );
                                    if (c.key === "status")
                                      return <td key={c.key}>{row._status}</td>;
                                    return <td key={`${c.key}-${idx}`}></td>;
                                  })}
                                </tr>

                                {isOpen && (
                                  <tr className="sr-product-row">
                                    <td
                                      colSpan={visibleCols.length}
                                      style={{ textAlign: "left" }}
                                    >
                                      {isLoad ? (
                                        <div className="sr-loading-inline">
                                          <FaSyncAlt className="fa-spin" />
                                          Loading invoice products...
                                        </div>
                                      ) : detail?.error ? (
                                        <Alert
                                          color="danger"
                                          style={{ margin: 0 }}
                                        >
                                          {detail.error}
                                        </Alert>
                                      ) : (
                                        <div style={{ padding: "4px 0" }}>
                                          <div
                                            style={{
                                              fontWeight: 800,
                                              marginBottom: 8,
                                              color: "#0f172a",
                                            }}
                                          >
                                            Products of {row._fullInvoice}
                                          </div>

                                          {!detail?.products?.length ? (
                                            <div className="sr-muted">
                                              No products found.
                                            </div>
                                          ) : (
                                            <div
                                              style={{
                                                border: "1px solid #e5e7eb",
                                                borderRadius: 12,
                                                overflow: "hidden",
                                              }}
                                            >
                                              <table
                                                style={{
                                                  width: "100%",
                                                  borderCollapse: "collapse",
                                                }}
                                              >
                                                <thead>
                                                  <tr
                                                    style={{
                                                      background: "#f8fafc",
                                                    }}
                                                  >
                                                    <th
                                                      style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                          "1px solid #e5e7eb",
                                                        textAlign: "left",
                                                        fontSize: 12,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      Product
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                          "1px solid #e5e7eb",
                                                        textAlign: "center",
                                                        fontSize: 12,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      HSN
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                          "1px solid #e5e7eb",
                                                        textAlign: "center",
                                                        fontSize: 12,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      Qty
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                          "1px solid #e5e7eb",
                                                        textAlign: "center",
                                                        fontSize: 12,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      Unit
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                          "1px solid #e5e7eb",
                                                        textAlign: "center",
                                                        fontSize: 12,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      Product Basic Price
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                          "1px solid #e5e7eb",
                                                        textAlign: "right",
                                                        fontSize: 12,
                                                        textTransform:
                                                          "uppercase",
                                                      }}
                                                    >
                                                      Product Basic Total
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {detail.products.map((p) => (
                                                    <tr key={p._id}>
                                                      <td
                                                        style={{
                                                          padding: "8px 10px",
                                                          borderBottom:
                                                            "1px solid #f1f5f9",
                                                          textAlign: "left",
                                                        }}
                                                      >
                                                        {p.productName}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "8px 10px",
                                                          borderBottom:
                                                            "1px solid #f1f5f9",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        {p.hsn || "-"}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "8px 10px",
                                                          borderBottom:
                                                            "1px solid #f1f5f9",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        {p.qty}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "8px 10px",
                                                          borderBottom:
                                                            "1px solid #f1f5f9",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        {p.unit || "-"}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "8px 10px",
                                                          borderBottom:
                                                            "1px solid #f1f5f9",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        {p.price || "-"}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: "8px 10px",
                                                          borderBottom:
                                                            "1px solid #f1f5f9",
                                                          textAlign: "right",
                                                          fontWeight: 700,
                                                        }}
                                                      >
                                                        {money(p.basic)}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      {visibleCols.map((c, idx) => {
                        if (c.key === "invoice")
                          return <td key={c.key}>Final Total</td>;
                        if (c.key === "basic")
                          return (
                            <td key={c.key}>{money(grandTotals.basic)}</td>
                          );
                        if (c.key === "cgst")
                          return <td key={c.key}>{money(grandTotals.cgst)}</td>;
                        if (c.key === "sgst")
                          return <td key={c.key}>{money(grandTotals.sgst)}</td>;
                        if (c.key === "igst")
                          return <td key={c.key}>{money(grandTotals.igst)}</td>;
                        if (c.key === "roundOff")
                          return (
                            <td key={c.key}>{money(grandTotals.roundOff)}</td>
                          );
                        if (c.key === "grand")
                          return (
                            <td key={c.key}>{money(grandTotals.grand)}</td>
                          );
                        return <td key={`${c.key}-${idx}`}></td>;
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="picklist">
                {BASE_COLS.map((col) => (
                  <div key={col.key} className="pickitem">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="picklist" style={{ flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = BASE_COLS.find((x) => x.key === key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        className="pickitem"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
